import React, { useState, useEffect, useCallback } from "react";
import { ClipLoader } from "react-spinners";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
} from "reactstrap";
import moment from "moment";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

import api from "../../../../../services/api";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import { useTheme } from "../../../../../hooks/theme";
import { useCompanies } from "../../../../../hooks/companies"; // Importa o hook useCompanies
import { useLegend } from "../../../../../hooks/legend"; // Ajuste o caminho se necessário
import ModalFilters from "../../../../../components/CustomModalFilters";

export default function ChartOcorrenciasEmAberto({
  deferLoad = false,
  onRegisterFetch,
}) {
  const { darkMode } = useTheme();
  const { empresa, transportadora, arrayParams } = useCompanies(); // Obtém as seleções de empresa e transportadora
  const { legendMode } = useLegend();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const color = darkMode ? "#e9ecef" : "#1e1e2f";
  const [modalFilters, setModalFilters] = useState(false);
  const [dataInicial, setDataInicial] = useState(
    moment().subtract(90, "days").startOf("month")
  );
  const [dataFinal, setDataFinal] = useState(moment().endOf("month"));
  const [auxDtInicial, setAuxDtInicial] = useState(
    moment().subtract(90, "days").startOf("month")
  );
  const [auxDtFinal, setAuxDtFinal] = useState(moment().endOf("month"));

  async function loadCharts() {
    try {
      setLoading(true);

      const params = {
        dataInicial: auxDtInicial.format("YYYY-MM-DD 00:00:00"),
        dataFinal: auxDtFinal.format("YYYY-MM-DD 23:59:59"),
      };

        if (arrayParams.embarcadores) {
          params.embarcadores = arrayParams.embarcadores;
        }

        if (arrayParams.transportadoras) {
          params.transportadoras = arrayParams.transportadoras;
        }

      const response = await api.get("/dashboard/custo/frete-por-negocio", {
        params,
      });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (onRegisterFetch) onRegisterFetch(loadCharts);
    if (!deferLoad) {
      if (empresa) loadCharts(); // Carrega os gráficos quando empresa ou transportadora mudar
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxDtFinal, auxDtInicial, empresa, transportadora]); // Adiciona empresa e transportadora como dependências

  const CustomTooltip = (props) => {
    const { active } = props;

    if (active) {
      const { payload } = props;
      const payFormatted = payload.map((pay) => ({
        ref: "Qt.Cte",
        descricao: pay.payload.quantidade_cte,
        name: pay.name,
        value: formatCurrency(pay.value),
      }));
      return (
        <div
          className="tooltip-inner"
          style={{
            background: darkMode ? "#e9ecef" : "#1e1e2f",
            maxWidth: "500px",
            textAlign: "left",
          }}
        >
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>{`${payFormatted[0].ref}: `}</strong>
            {payFormatted[0].descricao}
          </p>
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>{`${payFormatted[0].name}: `}</strong>
            {payFormatted[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 5} // Ajuste este valor conforme necessário
        fill={darkMode ? "#FFFFFF" : "#333333"}
        textAnchor="middle"
      >
        {formatCurrency(value)}
      </text>
    );
  };

  const handleDateInicial = (e) => {
    setDataInicial(e);
  };

  const handleDateFinal = (e) => {
    setDataFinal(e);
  };

  const toggleModalFilters = () => {
    setModalFilters(!modalFilters);
  };

  const onSubmit = useCallback(() => {
    setAuxDtInicial(dataInicial);
    setAuxDtFinal(dataFinal);
    setModalFilters(!modalFilters);
  }, [dataFinal, dataInicial, modalFilters]);

  return (
    <Card
      style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}
      className="card-chart"
    >
      <ModalFilters
        open={modalFilters}
        toggle={toggleModalFilters}
        handleDateInicial={handleDateInicial}
        dataInicial={dataInicial}
        handleDateFinal={handleDateFinal}
        dataFinal={dataFinal}
        onSubmit={() => onSubmit()}
      />
      <CardHeader>
        <Row style={{ alignItems: "center" }}>
          <Col>
            <h4>Frete por Negócio</h4>
          </Col>
          <Col style={{ textAlign: "right" }}>
            <Button
              className="btn-sm dashboard-period-button"
              color="info"
              onClick={toggleModalFilters}
            >
              Período
            </Button>
          </Col>
        </Row>

        <CardTitle>Custo de frete por Negócio e por período</CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div style={{ textAlign: "center" }}>
            <ClipLoader
              sizeUnit="px"
              size={90}
              color="#1d8cf8"
              loading={loading}
            />
            <br />
            <br />
            <h1 className="text-info" style={{ textAlign: "center" }}>
              Carregando ...
            </h1>
          </div>
        ) : (
          <Row className="justify-content-center">
            <Col>
              <ResponsiveContainer width="100%" height={250}>
                {data.length > 0 ? (
                  <BarChart
                    data={data}
                    margin={{ top: 30, right: 20, left: 20, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="bar2" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="20%"
                          stopColor="#1d8cf8"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="90%"
                          stopColor="#1d8cf8"
                          stopOpacity={0.1}
                        />
                        <stop
                          offset="90%"
                          stopColor="#1d8cf8"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      stroke="rgba(29,140,248, 0.1)"
                      strokeWidth={1}
                    />
                    <XAxis
                      dataKey="descricao"
                      stroke={color}
                      style={{ fontWeight: 100 }}
                    />
                    <YAxis
                      tickFormatter={(tick) => formatCurrency(tick)}
                      stroke={color}
                      style={{ fontWeight: 100 }}
                      width={100}
                    />
                    <Tooltip content={(props) => CustomTooltip(props)} />
                    <Legend />
                    <Bar
                      name="Frete"
                      dataKey="custo_frete"
                      type="monotone"
                      stroke="#0096FF"
                      fillOpacity={1}
                      fill="url(#bar2)"
                    >
                      {legendMode && (
                        <LabelList
                          dataKey="custo_frete"
                          content={CustomLabel}
                        />
                      )}
                    </Bar>
                  </BarChart>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <h5>Nenhum dado para apresentar!</h5>
                  </div>
                )}
              </ResponsiveContainer>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
}
